import { useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { AiFillCamera } from 'react-icons/ai';
import * as yup from 'yup';
import slugify from 'slugify';
import { useTranslation } from 'react-i18next';
import auth from '../../../apis/auth';
import cms from '../../../apis/cms';
import {
  CREATE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import AutocompleteSelect from '../../Form/AutocompleteSelect';
import Upload from '../../Form/Upload';
import context from '../context';
import { CategoriesOptionsType, CategoryType } from './Form';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (item: any) => void;
  onEdited?: (item: any) => void;
  onDelete?: () => void;
  Item?: null | any;
  pageGroup: any;
}

interface FormData {
  filter_id: {
    label: string | number;
    value: string | number;
  };

  file?: FileList;
  file_id?: string;
}

const createFormData = yup.object().shape({
  filter_id: yup
    .object()
    .shape({
      value: yup.string().required('El filtro es obligatorio'),
      label: yup.string().required('El filtro es obligatorio'),
    })
    .required('El filtro es obligatorio'),
});

const ItemForm: React.FC<Props> = ({
  onClose,
  isOpen,
  Item,
  onCreated,
  onEdited,
  pageGroup: pageGroupProps,
}) => {
  const { formState, handleSubmit, register, setValue, reset, control } =
    useForm({
      resolver: yupResolver(createFormData),
    });

  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });

  const { pageGroup, element, page } = useContext(context);
  const { t } = useTranslation();
  const toast = useToast();
  const { errors } = formState;
  const [loading, setLoading] = useState(false);

  const [filtersOptions, setFiltersOptions] = useState<CategoriesOptionsType[]>(
    [],
  );
  const [filters, setFilters] = useState<CategoryType[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function getFilter() {
    const { data: apiData } = await auth.get<any>(
      `bridge/vtex/api/catalog_system/pub/facets/search/${page?.vtex_query}`,
    );

    const filtersArray =
      apiData?.SpecificationFilters[`${pageGroup?.category_id}`];
    if (filtersArray.length >= 1) {
      setFilters(filtersArray);
      setFiltersOptions(
        filtersArray.map((filter: CategoryType) => {
          return {
            value: slugify(filter?.Name, { lower: true }),
            label: filter?.Name,
          };
        }),
      );
    }
  }

  useEffect(() => {
    getFilter();
    if (!Item) return;
    setValue(
      'filter_id',
      filtersOptions.find(
        (filter: CategoriesOptionsType) =>
          filter?.label === Item?.filter_data?.name,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Item, pageGroupProps]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const { ...rest } = data;
      setLoading(true);

      let fileReturn;
      if (data?.file && data?.file[0]) {
        const multipart = new FormData();
        multipart.append('file', data.file[0]);
        const { data: FileApi } = await cms.rest.post<any>(
          '/upload',
          multipart,
        );
        fileReturn = FileApi;
      }

      if (!Item) {
        const body: any = {
          ...rest,
          name: data.filter_id?.label,
          page_group_id: pageGroup?.id,
          element_id: element?.id,
          file_id: fileReturn ? fileReturn?.id : null,
        };

        if (fileReturn) {
          body.file_id = fileReturn.id;
        }

        if (data?.filter_id) {
          const selectedFilter: CategoryType | undefined = filters.find(
            (filter: CategoryType) => {
              return filter?.Name === data?.filter_id?.label;
            },
          );

          body.filter_data = {
            id: data?.filter_id?.value,
            name: data?.filter_id?.label,
            query: `${selectedFilter?.Map}:${selectedFilter?.Name}`,
          };
        }

        delete body.file;
        delete body.filter_id;

        const groupNewElement = await createGroupElement({
          variables: {
            createGroupElementInput: body,
          },
        });

        onClose();
        if (onCreated) onCreated(groupNewElement?.data?.createGroupElement);
        toast({
          title: t('success'),
          description: t('itemCreated'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const body: any = {
          ...rest,
          id: Item?.id,
          name: data.filter_id?.label,
          page_group_id: pageGroup?.id,
          element_id: element?.id,
          filter_id: data.filter_id?.value,
        };

        if (fileReturn) {
          body.file_id = fileReturn.id;
        }

        if (data?.filter_id) {
          const selectedFilter: CategoryType | undefined = filters.find(
            (filter: CategoryType) => {
              return filter?.Name === data?.filter_id?.label;
            },
          );

          body.filter_data = {
            id: data?.filter_id?.value,
            name: data?.filter_id?.label,
            query: `${selectedFilter?.Map}:${selectedFilter?.Name}`,
          };
        }

        delete body.file;
        delete body.filter_id;

        const groupNewElement = await updateGroupElement({
          variables: {
            updateGroupElementInput: body,
          },
        });

        onClose();
        if (onEdited) onEdited(groupNewElement?.data?.updateGroupElement);
        toast({
          title: t('success'),
          description: t('itemUpdated'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description: err?.response?.data?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      reset();
      onClose();
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        maxH="90vh"
        as="form"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <ModalHeader>{t('filterField')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <AutocompleteSelect
              label={t('filterField')}
              control={control}
              error={errors.filter_id as FieldError | undefined}
              options={filtersOptions}
              name="filter_id"
            />
            {pageGroupProps?.type === 'image' && (
              <Upload
                label="Imagem"
                acceptedFileTypes="image/*"
                alignSelf="center"
                icon={AiFillCamera}
                {...register('file')}
                url={Item?.file?.id ? `${Item?.file?.url}` : ''}
                idFileApi={Item?.file?.id}
              />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={4}>
            <Button onClick={onClose}>{t('close')}</Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              {t('save')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ItemForm;
